import React, { useState, useEffect } from 'react';
import { useCantonData } from '../../hooks/useCantonData';
import { API_BASE } from '../../utils/constants';

// SEO metadata for this page
const SEO = {
  title: 'Node Client - CC Reward Forecasting & Reporting | Cantonstats',
  description: 'Per-tick CC reward projections, scenario analysis, and export-ready reporting for Canton validators. Institutional-grade network intelligence.',
  keywords: 'Canton validator, CC rewards, reward forecasting, Canton reporting, network intelligence, validator analytics',
};

export function DownloadTab() {
  const { data } = useCantonData();

  // Set page-specific SEO metadata
  useEffect(() => {
    // Store original values
    const originalTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const originalDescription = metaDescription?.getAttribute('content');

    // Update title and description
    document.title = SEO.title;
    if (metaDescription) {
      metaDescription.setAttribute('content', SEO.description);
    }

    // Cleanup on unmount - restore original values
    return () => {
      document.title = originalTitle;
      if (metaDescription && originalDescription) {
        metaDescription.setAttribute('content', originalDescription);
      }
    };
  }, []);

  const [formData, setFormData] = useState({
    organisationName: '',
    payoutAddress: '',
    telegram: '',
    slack: '',
    deployment: '',
    referral: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/download-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit request. Please try again.');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid =
    formData.organisationName.trim() &&
    formData.payoutAddress.trim() &&
    formData.telegram.trim() &&
    formData.deployment;

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '6px',
    color: '#ffffff',
    fontSize: '12px',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '11px',
    fontWeight: '500',
    color: '#9ca3af',
    letterSpacing: '0.025em',
  };

  const requiredStar = {
    color: '#22d3ee',
    marginLeft: '4px',
  };

  if (submitted) {
    return (
      <div style={{
        maxWidth: '500px',
        margin: '100px auto',
        padding: '48px',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)',
        borderRadius: '24px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        textAlign: 'center',
      }}>
        <div style={{
          width: '72px',
          height: '72px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.2) 0%, rgba(34, 211, 238, 0.05) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 28px',
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2">
            <polyline points="20,6 9,17 4,12" />
          </svg>
        </div>
        <h2 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '12px', color: '#ffffff', letterSpacing: '-0.02em' }}>
          Request Submitted
        </h2>
        <p style={{ color: '#6b7280', fontSize: '15px', lineHeight: '1.7' }}>
          We'll review your request and reach out via Telegram or Slack.
        </p>
      </div>
    );
  }

  return (
    <article style={{
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '48px 24px 32px',
    }}>
      {/* Hero Section */}
      <div style={{
        textAlign: 'center',
        marginBottom: '48px',
        maxWidth: '600px',
        margin: '0 auto 48px',
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '100px',
          marginBottom: '16px',
        }}>
          <span style={{
            width: '5px',
            height: '5px',
            borderRadius: '50%',
            background: '#ffffff',
          }} />
          <span style={{ fontSize: '10px', fontWeight: '600', color: '#ffffff', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Private Network Intelligence
          </span>
        </div>

        <h1 style={{
          fontSize: 'clamp(28px, 4vw, 40px)',
          fontWeight: '600',
          color: '#ffffff',
          marginBottom: '12px',
          lineHeight: '1.1',
          letterSpacing: '-0.03em',
        }}>
          CC Reward Forecasting
        </h1>

        <p style={{
          color: '#6b7280',
          fontSize: '14px',
          lineHeight: '1.6',
          maxWidth: '480px',
          margin: '0 auto',
        }}>
          Per-tick projections with confidence intervals for validators, super validators, and application providers.
        </p>
      </div>

      {/* Main Content Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '32px',
        alignItems: 'stretch',
      }}>
        {/* Left Column: Features */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Feature Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
            {[
              {
                title: 'Per-tick Forecasting',
                desc: 'CC projections with confidence intervals based on participation weight and network dynamics.',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.5">
                    <path d="M2 12h4l3-9 6 18 3-9h4" />
                  </svg>
                )
              },
              {
                title: 'Scenario Analysis',
                desc: 'Model governance outcomes, burn rate adjustments, and network growth projections.',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
                  </svg>
                )
              },
              {
                title: 'Export-Ready Reporting',
                desc: 'CSV exports and API access for treasury reconciliation workflows.',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.5">
                    <path d="M12 3v12M8 11l4 4 4-4" />
                    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                  </svg>
                )
              },
              {
                title: 'Deviation Alerts',
                desc: 'Real-time notifications when actual rewards diverge from projections.',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.5">
                    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                    <path d="M4 2L2 4M22 2l-2 2" />
                  </svg>
                )
              },
            ].map((feature, i) => (
              <div key={i} style={{
                padding: '16px',
                background: 'transparent',
                border: 'none',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                flex: 1,
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}>
                  <div style={{ flexShrink: 0, marginTop: '2px' }}>
                    {feature.icon}
                  </div>
                  <div>
                    <h3 style={{
                      color: '#ffffff',
                      fontSize: '13px',
                      fontWeight: '600',
                      marginBottom: '4px',
                      letterSpacing: '-0.01em',
                    }}>
                      {feature.title}
                    </h3>
                    <p style={{
                      color: '#6b7280',
                      fontSize: '12px',
                      lineHeight: '1.5',
                      margin: 0,
                    }}>
                      {feature.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Trust Indicators */}
          <div style={{
            marginTop: '12px',
            padding: '14px 16px',
            background: 'transparent',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '32px',
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>Read-only access</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>No custody</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
              </svg>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>Real-time sync</span>
            </div>
          </div>
        </div>

        {/* Right Column: Form */}
        <div style={{
          background: 'transparent',
          borderRadius: '16px',
          border: 'none',
          padding: '24px',
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#ffffff',
            marginBottom: '6px',
            letterSpacing: '-0.02em',
          }}>
            Request Access
          </h2>
          <p style={{
            color: '#6b7280',
            fontSize: '12px',
            marginBottom: '20px',
            lineHeight: '1.5',
          }}>
            We'll configure the client for your infrastructure.
          </p>

          <form onSubmit={handleSubmit} aria-label="Request access to Cantonstats Node Client">
            {error && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '12px',
                padding: '14px 16px',
                marginBottom: '24px',
                color: '#ef4444',
                fontSize: '13px',
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Organisation Name */}
              <div>
                <label style={labelStyle}>
                  Organisation<span style={requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="organisationName"
                  value={formData.organisationName}
                  onChange={handleChange}
                  placeholder="Company or individual name"
                  required
                  style={inputStyle}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(34, 211, 238, 0.5)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.03)';
                  }}
                />
              </div>

              {/* Payout Address */}
              <div>
                <label style={labelStyle}>
                  Payout Rewards Address<span style={requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="payoutAddress"
                  value={formData.payoutAddress}
                  onChange={handleChange}
                  placeholder="party-xxxxxxxx"
                  required
                  style={{ ...inputStyle, fontFamily: 'monospace' }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(34, 211, 238, 0.5)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.03)';
                  }}
                />
              </div>

              {/* Two column for contact */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>
                    Telegram<span style={requiredStar}>*</span>
                  </label>
                  <input
                    type="text"
                    name="telegram"
                    value={formData.telegram}
                    onChange={handleChange}
                    placeholder="@handle"
                    required
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'rgba(34, 211, 238, 0.5)';
                      e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.background = 'rgba(255, 255, 255, 0.03)';
                    }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>
                    Slack
                  </label>
                  <input
                    type="text"
                    name="slack"
                    value={formData.slack}
                    onChange={handleChange}
                    placeholder="@handle"
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'rgba(34, 211, 238, 0.5)';
                      e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.background = 'rgba(255, 255, 255, 0.03)';
                    }}
                  />
                </div>
              </div>

              {/* Deployment Type */}
              <div>
                <label style={labelStyle}>
                  Deployment<span style={requiredStar}>*</span>
                </label>
                <select
                  name="deployment"
                  value={formData.deployment}
                  onChange={handleChange}
                  required
                  style={{
                    ...inputStyle,
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6,9 12,15 18,9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 16px center',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(34, 211, 238, 0.5)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.03)';
                  }}
                >
                  <option value="" disabled style={{ background: '#0f1218', color: '#6b7280' }}>
                    Select environment
                  </option>
                  <option value="docker" style={{ background: '#0f1218', color: '#ffffff' }}>
                    Docker
                  </option>
                  <option value="kubernetes" style={{ background: '#0f1218', color: '#ffffff' }}>
                    Kubernetes
                  </option>
                  <option value="both" style={{ background: '#0f1218', color: '#ffffff' }}>
                    Both
                  </option>
                  <option value="custom" style={{ background: '#0f1218', color: '#ffffff' }}>
                    Custom
                  </option>
                </select>
              </div>

              {/* Referral */}
              <div>
                <label style={labelStyle}>
                  How did you find us?
                </label>
                <input
                  type="text"
                  name="referral"
                  value={formData.referral}
                  onChange={handleChange}
                  placeholder="Twitter, Telegram, Discord..."
                  style={inputStyle}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(34, 211, 238, 0.5)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.03)';
                  }}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isFormValid || submitting}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  marginTop: '4px',
                  background: isFormValid && !submitting
                    ? '#ffffff'
                    : 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  color: isFormValid && !submitting ? '#0a0c10' : '#6b7280',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: isFormValid && !submitting ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  if (isFormValid && !submitting) {
                    e.currentTarget.style.background = '#f0f0f0';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(255, 255, 255, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isFormValid && !submitting) {
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <span style={{
                      width: '14px',
                      height: '14px',
                      border: '2px solid transparent',
                      borderTopColor: '#0a0c10',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                    }} />
                    Submitting...
                  </>
                ) : (
                  <>
                    Request Access
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* JSON-LD Structured Data for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Cantonstats Node Client",
        "applicationCategory": "FinanceApplication",
        "operatingSystem": "Linux",
        "description": SEO.description,
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "provider": {
          "@type": "Organization",
          "name": "Cantonstats",
          "url": "https://cantonstats.io"
        },
        "featureList": [
          "Per-tick CC Forecasting",
          "Scenario Analysis",
          "Export-Ready Reporting",
          "Deviation Alerts"
        ]
      })}} />

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </article>
  );
}
